import { z } from 'zod';
import { GENDERS, MARITAL_STATUSES, PROFESSIONAL_DETAILS, DESCRIBES_YOU, YES_NO_UNSURE, STAGES, } from './enums.js';
import { MIN_HEIGHT_CM, MAX_HEIGHT_CM } from './height.js';
/** Trim, then treat an empty string as absent. Keeps blank inputs out of the database. */
const optionalText = (max = 2000) => z.string().trim().max(max).transform((v) => (v === '' ? undefined : v)).optional();
const optionalSelect = (values) => z.enum(values).optional().or(z.literal('').transform(() => undefined));
const optionalCount = z.coerce.number().int().min(0).max(20).optional();
const optionalAge = z.coerce.number().int().min(18).max(90).optional();
const optionalHeight = z.coerce.number().int().min(MIN_HEIGHT_CM).max(MAX_HEIGHT_CM).optional();
const dateOfBirth = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker')
    .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    if (Number.isNaN(d.getTime()))
        return false;
    const now = Date.now();
    const oldest = Date.UTC(new Date().getUTCFullYear() - 90, 0, 1);
    return d.getTime() <= now && d.getTime() >= oldest;
}, 'Enter a real date of birth');
/** ISO 3166-1 alpha-2. Not enumerated, so new countries never need a code change. */
const countryCode = z.string().trim().length(2).toUpperCase();
export const photoSchema = z.object({
    public_id: z.string().min(1),
    url: z.string().url(),
    thumbnail_url: z.string().url().optional(),
    width: z.coerce.number().int().positive(),
    height: z.coerce.number().int().positive(),
    bytes: z.coerce.number().int().positive(),
});
export const submissionSchema = z.object({
    // Required
    full_name: z.string().trim().min(1, 'Please enter your full name').max(150),
    contact_number: z.string().trim().min(4, 'Please enter a contact number').max(30),
    contact_country: countryCode.default('IN'),
    date_of_birth: dateOfBirth,
    gender: z.enum(GENDERS),
    marital_status: z.enum(MARITAL_STATUSES),
    consent: z.literal(true, { errorMap: () => ({ message: 'Please agree before submitting' }) }),
    // Optional — personal
    maslak: optionalText(150),
    place_of_birth: optionalText(150),
    height_cm: optionalHeight,
    // Optional — education & work
    education_qualification: optionalText(300),
    professional_details: optionalSelect(PROFESSIONAL_DETAILS),
    what_do_you_do: optionalText(),
    hobbies_interests: optionalText(),
    best_describes_you: optionalSelect(DESCRIBES_YOU),
    // Optional — family
    father_name: optionalText(150),
    father_contact_number: optionalText(30),
    father_contact_country: countryCode.optional(),
    father_occupation: optionalText(200),
    mother_name: optionalText(150),
    mother_contact_number: optionalText(30),
    mother_contact_country: countryCode.optional(),
    mother_occupation: optionalText(200),
    number_of_sisters: optionalCount,
    number_of_brothers: optionalCount,
    maternal_grandparents: optionalText(),
    paternal_grandparents: optionalText(),
    // Optional — address
    residential_address: optionalText(),
    landmark: optionalText(300),
    // Optional — preferences
    pref_age_min: optionalAge,
    pref_age_max: optionalAge,
    pref_height_cm: optionalHeight,
    pref_maslak: optionalText(150),
    pref_hijab_beard: optionalText(300),
    pref_qualities: optionalText(),
    pref_work_expectation: optionalText(),
    pref_other: optionalText(),
    open_to_revert: optionalSelect(YES_NO_UNSURE),
    open_to_divorcee_widow: optionalSelect(YES_NO_UNSURE),
    photos: z.array(photoSchema).max(6, 'At most six photographs').optional(),
    /** Honeypot. Hidden from sight and from assistive technology; only bots fill it. */
    website: z.string().max(0, 'Rejected').optional(),
})
    .refine((v) => v.pref_age_min === undefined || v.pref_age_max === undefined || v.pref_age_min <= v.pref_age_max, { message: 'Preferred age range is back to front', path: ['pref_age_max'] });
/** Editing from the dashboard: every field optional, consent and honeypot irrelevant. */
export const profileEditSchema = submissionSchema
    .innerType()
    .omit({ consent: true, website: true, photos: true })
    .partial()
    .extend({ stage: z.enum(STAGES).optional(), is_starred: z.boolean().optional() });
export const noteSchema = z.object({
    body: z.string().trim().min(1, 'Write something first').max(5000),
});
export const loginSchema = z.object({
    email: z.string().trim().email('Enter a valid email address'),
    password: z.string().min(1, 'Enter your password'),
});
const passwordPair = {
    new_password: z.string().min(8, 'Use at least eight characters').max(200),
    confirm_password: z.string(),
};
export const changePasswordSchema = z.object({
    current_email: z.string().trim().email(),
    current_password: z.string().min(1),
    ...passwordPair,
}).refine((v) => v.new_password === v.confirm_password, {
    message: 'The two passwords do not match', path: ['confirm_password'],
});
export const forgotPasswordSchema = z.object({
    recovery_email: z.string().trim().email(),
    recovery_password: z.string().min(1),
    ...passwordPair,
}).refine((v) => v.new_password === v.confirm_password, {
    message: 'The two passwords do not match', path: ['confirm_password'],
});
//# sourceMappingURL=schemas.js.map